if (!customElements.get('bp-copy-product')) {
  customElements.define('bp-copy-product', class extends HTMLElement {
    connectedCallback() {
      this.onClick = this.handleClick.bind(this);
      this.onPop = () => this.loadCopy(new URL(location.href), false);
      this.onWake = () => { if (document.visibilityState === 'visible') this.loadCopy(new URL(location.href), false); };
      this.addEventListener('click', this.onClick);
      this.onKey = this.handleGalleryKey.bind(this);
      this.addEventListener('keydown', this.onKey);
      const firstThumbnail = this.querySelector('[data-copy-thumbnail]');
      if (firstThumbnail) this.selectPhoto(firstThumbnail);
      this.addEventListener('submit', event => { if (this.busy) event.preventDefault(); });
      window.addEventListener('popstate', this.onPop);
      document.addEventListener('visibilitychange', this.onWake);
      if (this.dataset.draftPreview === 'true') {
        this.querySelectorAll('[data-copy-choice]').forEach(choice => {
          const destination = new URL(location.href);
          destination.searchParams.set('variant', choice.dataset.copyChoice);
          choice.href = destination.toString();
        });
      }
      const requested = new URL(location.href).searchParams.get('variant');
      if (requested && requested !== this.dataset.selectedCopy) this.blockPurchase();
      // Liquid cannot distinguish an absent variant parameter from a deleted ID.
      // Only auto-select the sole copy after checking the actual browser URL.
      if (!requested && !this.dataset.selectedCopy && this.dataset.autoSelect) {
        const destination = new URL(location.href);
        destination.searchParams.set('variant', this.dataset.autoSelect);
        this.loadCopy(destination, false, true);
      }
    }
    disconnectedCallback() {
      this.request?.abort();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      window.removeEventListener('popstate', this.onPop);
      document.removeEventListener('visibilitychange', this.onWake);
    }
    blockPurchase() {
      this.querySelectorAll('form button, form input[name="id"]').forEach(el => { el.disabled = true; });
    }
    updateSharing(section) {
      const template = section.querySelector('template[data-copy-sharing-meta]');
      if (!template) return;
      const title = template.content.querySelector('title');
      if (title) document.title = title.textContent;
      const allowed = new Set(['description', 'og:site_name', 'og:url', 'og:title', 'og:type', 'og:description',
        'og:image', 'og:image:secure_url', 'og:image:width', 'og:image:height', 'og:image:alt',
        'og:price:amount', 'og:price:currency', 'twitter:site', 'twitter:card', 'twitter:title',
        'twitter:description', 'twitter:image', 'twitter:image:alt']);
      const values = new Map();
      template.content.querySelectorAll('meta').forEach(meta => {
        const attribute = meta.hasAttribute('property') ? 'property' : 'name';
        const key = meta.getAttribute(attribute);
        if (allowed.has(key)) values.set(key, { attribute, content: meta.getAttribute('content') || '' });
      });
      document.head.querySelectorAll('meta[name], meta[property]').forEach(meta => {
        const key = meta.getAttribute('property') || meta.getAttribute('name');
        if (allowed.has(key)) meta.remove();
      });
      values.forEach(({ attribute, content }, key) => {
        const meta = document.createElement('meta');
        meta.setAttribute(attribute, key);
        meta.setAttribute('content', content);
        document.head.append(meta);
      });
    }
    selectPhoto(thumbnail) {
      this.querySelectorAll('[data-copy-slide]').forEach(slide => {
        slide.querySelector('bp-image-magnifier')?.stop?.();
        slide.hidden = slide.id !== thumbnail.dataset.copyThumbnail;
      });
      this.querySelectorAll('[data-copy-thumbnail]').forEach(link => {
        if (link === thumbnail) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }
    handleGalleryKey(event) {
      const thumbnail = event.target.closest('[data-copy-thumbnail]');
      if (!thumbnail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const thumbnails = [...this.querySelectorAll('[data-copy-thumbnail]')];
      const index = thumbnails.indexOf(thumbnail);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? thumbnails.length - 1
        : (index + (event.key === 'ArrowRight' ? 1 : -1) + thumbnails.length) % thumbnails.length;
      this.selectPhoto(thumbnails[next]);
      thumbnails[next].focus({ preventScroll: true });
      thumbnails[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    handleClick(event) {
      const thumbnail = event.target.closest('[data-copy-thumbnail]');
      if (thumbnail && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        this.selectPhoto(thumbnail);
        return;
      }
      const choice = event.target.closest('[data-copy-choice]');
      if (choice && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        const destination = new URL(choice.href);
        if (destination.origin !== location.origin || destination.pathname !== location.pathname) return;
        event.preventDefault();
        this.loadCopy(destination, true);
        return;
      }
      const photo = event.target.closest('[data-copy-zoom]');
      const dialog = this.querySelector('dialog');
      if (photo && dialog?.showModal && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        const image = dialog.querySelector('img');
        image.src = photo.href;
        image.alt = photo.dataset.photoAlt || photo.querySelector('img')?.alt || '';
        this.querySelectorAll('bp-image-magnifier').forEach(inspector => inspector.stop?.());
        dialog.showModal();
      }
      if (event.target.closest('[data-copy-close]') || event.target === dialog) dialog?.close();
    }
    async loadCopy(destination, push, pinSelection = false) {
      if (destination.origin !== location.origin || destination.pathname !== location.pathname) return;
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.blockPurchase();
      try {
        const url = new URL(destination);
        url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('copy_load');
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = documentResult.getElementById(this.id);
        if (!next || next.dataset.product !== this.dataset.product) throw new Error('copy_mismatch');
        const requested = destination.searchParams.get('variant');
        if (requested && next.dataset.selectedCopy !== requested) throw new Error('copy_unavailable');
        if (controller.signal.aborted) return;
        if (push) history.pushState({}, '', destination);
        else if (pinSelection) history.replaceState({}, '', destination);
        this.updateSharing(next);
        const imported = document.importNode(next, true);
        this.replaceWith(imported);
        if (push && requested) (imported.querySelector(`[data-copy-choice="${CSS.escape(requested)}"]`) || imported.querySelector('h1'))?.focus({ preventScroll: true });
      } catch (error) {
        if (controller.signal.aborted) return;
        this.removeAttribute('aria-busy');
        const status = this.querySelector('[data-copy-status]');
        status.textContent = this.dataset.error;
        status.focus();
      }
    }
  });
}
